import _, { parseInt } from 'lodash';
import { adminClient as apolloClient } from '@/store/apollo';

import SearchCmsGql from '@/extensions/tickets/store/graphql/queries/SearchCms.gql';
import TicketsPaginatedGql from '@/extensions/tickets/store/graphql/queries/TicketsPaginated.gql';
import TicketGql from '@/extensions/tickets/store/graphql/queries/Ticket.gql';
import TicketsWindowGql from '@/extensions/tickets/store/graphql/queries/TicketsWindow.gql';
import TicketTimeEntriesGql from '@/extensions/tickets/store/graphql/queries/TicketTimeEntries.gql';
import TicketAssignmentCandidatesGql from '@/extensions/tickets/store/graphql/queries/TicketAssignmentCandidates.gql';
import TicketRequesterCandidatesGql from '@/extensions/tickets/store/graphql/queries/TicketRequesterCandidates.gql';
import TicketStatusCountsGql from '@/extensions/tickets/store/graphql/queries/TicketStatusCounts.gql';
import TicketUnreadCountGql from '@/extensions/tickets/store/graphql/queries/TicketUnreadCount.gql';
import SupportTiersGql from '@/extensions/tickets/store/graphql/queries/SupportTiers.gql';
import CreateTicketGql from '@/extensions/tickets/store/graphql/mutations/CreateTicket.gql';
import UpdateTicketGql from '@/extensions/tickets/store/graphql/mutations/UpdateTicket.gql';
import DeleteTicketGql from '@/extensions/tickets/store/graphql/mutations/DeleteTicket.gql';
import MarkTicketSpamGql from '@/extensions/tickets/store/graphql/mutations/MarkTicketSpam.gql';
import CreateTicketReplyGql from '@/extensions/tickets/store/graphql/mutations/CreateTicketReply.gql';
import SaveTicketDraftGql from '@/extensions/tickets/store/graphql/mutations/SaveTicketDraft.gql';
import DiscardTicketDraftGql from '@/extensions/tickets/store/graphql/mutations/DiscardTicketDraft.gql';
import CreateTicketNoteGql from '@/extensions/tickets/store/graphql/mutations/CreateTicketNote.gql';
import UpdateTicketNoteGql from '@/extensions/tickets/store/graphql/mutations/UpdateTicketNote.gql';
import TicketNotesGql from '@/extensions/tickets/store/graphql/queries/TicketNotes.gql';
import TicketHistoryGql from '@/extensions/tickets/store/graphql/queries/TicketHistory.gql';
import SaveTicketTimeEntryGql from '@/extensions/tickets/store/graphql/mutations/SaveTicketTimeEntry.gql';
import DeleteTicketTimeEntryGql from '@/extensions/tickets/store/graphql/mutations/DeleteTicketTimeEntry.gql';
import StartSupportTimeEntryGql from '@/extensions/tickets/store/graphql/mutations/StartSupportTimeEntry.gql';
import StopSupportTimeEntryGql from '@/extensions/tickets/store/graphql/mutations/StopSupportTimeEntry.gql';
import PauseSupportTimeEntryGql from '@/extensions/tickets/store/graphql/mutations/PauseSupportTimeEntry.gql';
import ResumeSupportTimeEntryGql from '@/extensions/tickets/store/graphql/mutations/ResumeSupportTimeEntry.gql';
import TenantPlatformTicketsGql from '@/extensions/tickets/store/graphql/queries/TenantPlatformTickets.gql';
import TenantPlatformTicketGql from '@/extensions/tickets/store/graphql/queries/TenantPlatformTicket.gql';
import CreateTenantPlatformTicketGql from '@/extensions/tickets/store/graphql/mutations/CreateTenantPlatformTicket.gql';
import CreateTenantPlatformTicketReplyGql from '@/extensions/tickets/store/graphql/mutations/CreateTenantPlatformTicketReply.gql';
import AttachTenantPlatformTicketFilesGql from '@/extensions/tickets/store/graphql/mutations/AttachTenantPlatformTicketFiles.gql';
import TenantPlatformTicketUnreadCountGql from '@/extensions/tickets/store/graphql/queries/TenantPlatformTicketUnreadCount.gql';
import MarkTenantPlatformTicketReadGql from '@/extensions/tickets/store/graphql/mutations/MarkTenantPlatformTicketRead.gql';
import TenantPlatformClosedTicketCountGql from '@/extensions/tickets/store/graphql/queries/TenantPlatformClosedTicketCount.gql';
import TenantPlatformTicketContactFilterGql from '@/extensions/tickets/store/graphql/queries/TenantPlatformTicketContactFilter.gql';

// Every Int field of EX_TCKT_TicketInput. A select binds whatever the option
// carried, and a GraphQL id arrives as a string often enough that one uncoerced
// field fails the whole mutation ("Int cannot represent non-integer value"), so
// they are coerced here — at the api boundary every save passes through — rather
// than one at a time at each call site.
const INTEGER_FIELDS = ['progress'];

// The associations are object inputs mirroring the output type, so their id is
// the thing that needs coercing.
const OBJECT_ID_FIELDS = ['member', 'assignedUser', 'followupOf', 'project', 'milestone'];

const toInteger = (value) => {
    if (value === null || value === undefined || value === '') return null;

    const number = parseInt(value, 10);
    return Number.isNaN(number) ? null : number;
};

const cleaned = (input) => {
    const payload = _.removeDeepByKey(_.cloneDeep(input), '__typename');

    INTEGER_FIELDS.forEach((field) => {
        if (_.has(payload, field)) payload[field] = toInteger(payload[field]);
    });

    OBJECT_ID_FIELDS.forEach((field) => {
        const id = _.get(payload, `${field}.id`);
        if (id !== undefined) payload[field] = { id: toInteger(id) };
    });

    if (Array.isArray(payload.indicators)) {
        payload.indicators = payload.indicators
            .map((indicator) => ({ id: toInteger(indicator.id) }))
            .filter((indicator) => indicator.id !== null);
    }

    if (Array.isArray(payload.files)) {
        payload.files = payload.files.map((file) => ({ ...file, id: toInteger(file.id) }));
    }

    return payload;
};

// One more history entry than the detail shows per page, so it knows whether
// a "Mehr laden" is due without a count query.
export const HISTORY_PAGE = 10;

// upload:true = a fresh upload the backend still has to convert into a File;
// upload:false = a library file or one the ticket already holds.
const fileInputs = (files) => (files || []).map((file) => ({ id: parseInt(file.id), upload: file.upload !== false }));

export default {
    fetchPaginated: ({ cmsId, ...variables }) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: TicketsPaginatedGql,
                    variables: { ...variables, cms_id: cmsId ? parseInt(cmsId) : undefined },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.EX_TCKT_TicketsPaginated))
                .catch((err) => reject(err));
        }),

    fetch: (id) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: TicketGql,
                    variables: { id: parseInt(id), historyTake: HISTORY_PAGE + 1 },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.EX_TCKT_Ticket))
                .catch((err) => reject(err));
        }),

    fetchWindow: ({ from, to, cmsId, limit, filters, desk, includeUndated, excludeClosed }) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: TicketsWindowGql,
                    variables: {
                        from,
                        to,
                        cms_id: cmsId ? parseInt(cmsId) : undefined,
                        limit: limit || undefined,
                        desk: desk || undefined,
                        include_undated: includeUndated || undefined,
                        exclude_closed: excludeClosed || undefined,
                        filters: filters && filters.length ? filters : undefined,
                    },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.EX_TCKT_Tickets))
                .catch((err) => reject(err));
        }),
    fetchTimeEntries: (variables) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: TicketTimeEntriesGql,
                    variables,
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.EX_TCKT_TicketTimeEntries))
                .catch((err) => reject(err));
        }),

    fetchSupportTiers: () =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({ query: SupportTiersGql, fetchPolicy: 'cache-first' })
                .then(({ data }) => resolve(data.EX_TCKT_SupportTiers))
                .catch((err) => reject(err));
        }),

    fetchUnreadCount: (cmsId, desk) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: TicketUnreadCountGql,
                    variables: { cms_id: cmsId ? parseInt(cmsId) : undefined, desk: desk || undefined },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.EX_TCKT_TicketUnreadCount))
                .catch((err) => reject(err));
        }),

    fetchStatusCounts: (cmsId, desk) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: TicketStatusCountsGql,
                    variables: { cms_id: cmsId ? parseInt(cmsId) : undefined, desk: desk || undefined },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.EX_TCKT_TicketStatusCounts))
                .catch((err) => reject(err));
        }),

    // The website's own people — who a support ticket of OURS is with.
    fetchRequesterCandidates: (cmsId) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: TicketRequesterCandidatesGql,
                    variables: { cms_id: parseInt(cmsId, 10) },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.EX_TCKT_TicketRequesterCandidates))
                .catch((err) => reject(err));
        }),

    fetchAssignmentCandidates: (cmsId, desk) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: TicketAssignmentCandidatesGql,
                    variables: { cms_id: cmsId ? parseInt(cmsId) : undefined, desk: desk || undefined },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.EX_TCKT_TicketAssignmentCandidates))
                .catch((err) => reject(err));
        }),

    // Which customer a platform ticket is about. Lives here rather than inline
    // in a component so the create form and the WhatsApp starter ask the same
    // question the same way.
    // What a team member is told about. An empty list means "everything" -
    // absence is the default, not a blank slate.


    searchCms: (term) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: SearchCmsGql,
                    // The website OR the account behind it: an agency's ten
                    // websites are found by the agency's name.
                    variables: { filters: [{ key: 'customer', like: term }] },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(_.get(data, 'CP_CMSsPaginated.items') || []))
                .catch((err) => reject(err));
        }),

    create: ({ cmsId, ticket, body }) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CreateTicketGql,
                    variables: { cms_id: cmsId ? parseInt(cmsId) : undefined, ticket: cleaned(ticket), body },
                })
                .then(({ data }) => resolve(data.EX_TCKT_CreateTicket))
                .catch((err) => reject(err));
        }),

    update: (id, ticket) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: UpdateTicketGql,
                    variables: { id: parseInt(id), ticket: cleaned(ticket) },
                })
                .then(({ data }) => resolve(data.EX_TCKT_UpdateTicket))
                .catch((err) => reject(err));
        }),

    delete: (id) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({ mutation: DeleteTicketGql, variables: { id: parseInt(id) } })
                .then(({ data }) => resolve(data.EX_TCKT_DeleteTicket))
                .catch((err) => reject(err));
        }),

    markSpam: (id, unmark = false) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({ mutation: MarkTicketSpamGql, variables: { id: parseInt(id), unmark } })
                .then(({ data }) => resolve(data.EX_TCKT_MarkTicketSpam))
                .catch((err) => reject(err));
        }),

    // attachments: the composer's own objects ({ id, upload }) — a fresh one is
    // still an Upload and has to be converted server-side, exactly like the
    // communication composer's.
    // The ticket's ONE shared draft. Sends nothing. `instanceId` travels so the
    // saving tab can ignore the broadcast echo of its own write.
    saveDraft: (id, body, attachments, instanceId) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: SaveTicketDraftGql,
                    variables: {
                        id: parseInt(id),
                        body,
                        attachments: attachments || undefined,
                        instance_id: instanceId || undefined,
                    },
                })
                .then(({ data }) => resolve(data.EX_TCKT_SaveTicketDraft))
                .catch((err) => reject(err));
        }),

    discardDraft: (id, instanceId) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: DiscardTicketDraftGql,
                    variables: {
                        id: parseInt(id),
                        instance_id: instanceId || undefined,
                    },
                })
                .then(({ data }) => resolve(data.EX_TCKT_DiscardTicketDraft))
                .catch((err) => reject(err));
        }),

    createReply: (id, body, attachments, cc = [], bcc = []) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CreateTicketReplyGql,
                    variables: {
                        id: parseInt(id),
                        body,
                        attachments: attachments || undefined,
                        cc,
                        bcc,
                    },
                })
                .then(({ data }) => resolve(data.EX_TCKT_CreateTicketReply))
                .catch((err) => reject(err));
        }),

    // The history beyond the first page the ticket fetch carries: same order
    // (newest first), skip/take.
    fetchHistory: ({ ticketId, skip, take }) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: TicketHistoryGql,
                    variables: { ticket_id: parseInt(ticketId), skip, take },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.EX_TCKT_TicketHistory))
                .catch((err) => reject(err));
        }),

    fetchNotes: (id) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: TicketNotesGql,
                    variables: { ticket_id: parseInt(id) },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.EX_TCKT_TicketNotes))
                .catch((err) => reject(err));
        }),

    updateNote: (id, body) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: UpdateTicketNoteGql,
                    variables: { id: parseInt(id), body },
                })
                .then(({ data }) => resolve(data.EX_TCKT_UpdateTicketNote))
                .catch((err) => reject(err));
        }),
    createNote: (id, body) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CreateTicketNoteGql,
                    variables: { ticket_id: parseInt(id), body },
                })
                .then(({ data }) => resolve(data.EX_TCKT_CreateTicketNote))
                .catch((err) => reject(err));
        }),

    saveTimeEntry: ({ id, entry }) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: SaveTicketTimeEntryGql,
                    variables: { id: id ? parseInt(id) : undefined, entry: cleaned(entry) },
                })
                .then(({ data }) => resolve(data.EX_TCKT_SaveTicketTimeEntry))
                .catch((err) => reject(err));
        }),

    deleteTimeEntry: (id) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: DeleteTicketTimeEntryGql,
                    variables: { id: parseInt(id) },
                })
                .then(({ data }) => resolve(data.EX_TCKT_DeleteTicketTimeEntry))
                .catch((err) => reject(err));
        }),
    startSupportTimeEntry: ({ ticketId, newTicket, cmsId }) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: StartSupportTimeEntryGql,
                    variables: {
                        ticket_id: ticketId ? parseInt(ticketId) : undefined,
                        newTicket: newTicket ? cleaned(newTicket) : undefined,
                        cms_id: cmsId ? parseInt(cmsId) : undefined,
                    },
                })
                .then(({ data }) => resolve(data.EX_TCKT_StartSupportTimeEntry))
                .catch((err) => reject(err));
        }),

    pauseSupportTimeEntry: (id) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({ mutation: PauseSupportTimeEntryGql, variables: { id: parseInt(id) } })
                .then(({ data }) => resolve(data.EX_TCKT_PauseSupportTimeEntry))
                .catch((err) => reject(err));
        }),
    resumeSupportTimeEntry: (id) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({ mutation: ResumeSupportTimeEntryGql, variables: { id: parseInt(id) } })
                .then(({ data }) => resolve(data.EX_TCKT_ResumeSupportTimeEntry))
                .catch((err) => reject(err));
        }),
    stopSupportTimeEntry: (
        { id, durationSeconds, note, billable, ticketId, status }
    ) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: StopSupportTimeEntryGql,
                    variables: {
                        id: parseInt(id),
                        duration_seconds:
                            durationSeconds === null || durationSeconds === undefined
                                ? undefined
                                : parseInt(durationSeconds),
                        note,
                        billable,
                        // Where the time actually belongs, decided at stop.
                        ticket_id: ticketId ? parseInt(ticketId) : undefined,
                        status: status || undefined,
                    },
                })
                .then(({ data }) => resolve(data.EX_TCKT_StopSupportTimeEntry))
                .catch((err) => reject(err));
        }),

    // A CMS admin's window onto OUR platform desk (T-350,
    // docs/features/TENANT_PLATFORM_TICKETS.md).
    // One page: { data, total, per_page, current_page }.
    fetchTenantPlatformTickets: (cmsId, { page = 1, search = '', closed = false, mine = false } = {}) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: TenantPlatformTicketsGql,
                    variables: { cms_id: parseInt(cmsId), page: parseInt(page), search: search || null, closed, mine },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.EX_TCKT_TenantPlatformTickets))
                .catch((err) => reject(err));
        }),

    fetchTenantPlatformTicket: (cmsId, id) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: TenantPlatformTicketGql,
                    variables: { cms_id: parseInt(cmsId), id: parseInt(id) },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.EX_TCKT_TenantPlatformTicket))
                .catch((err) => reject(err));
        }),

    createTenantPlatformTicket: (cmsId, { subject, description, files }) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CreateTenantPlatformTicketGql,
                    variables: { cms_id: parseInt(cmsId), subject, description, files: fileInputs(files) },
                })
                .then(({ data }) => resolve(data.EX_TCKT_CreateTenantPlatformTicket))
                .catch((err) => reject(err));
        }),

    createTenantPlatformTicketReply: (cmsId, ticketId, body) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CreateTenantPlatformTicketReplyGql,
                    variables: { cms_id: parseInt(cmsId), ticket_id: parseInt(ticketId), body },
                })
                .then(({ data }) => resolve(data.EX_TCKT_CreateTenantPlatformTicketReply))
                .catch((err) => reject(err));
        }),

    fetchTenantPlatformTicketUnreadCount: (cmsId) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: TenantPlatformTicketUnreadCountGql,
                    variables: { cms_id: parseInt(cmsId) },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.EX_TCKT_TenantPlatformTicketUnreadCount))
                .catch((err) => reject(err));
        }),

    fetchTenantPlatformClosedTicketCount: (cmsId, { mine = false } = {}) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: TenantPlatformClosedTicketCountGql,
                    variables: { cms_id: parseInt(cmsId), mine },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.EX_TCKT_TenantPlatformClosedTicketCount))
                .catch((err) => reject(err));
        }),

    // The user's name when "Tickets von {name}" tells something apart, else null.
    fetchTenantPlatformTicketContactFilter: (cmsId) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: TenantPlatformTicketContactFilterGql,
                    variables: { cms_id: parseInt(cmsId) },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.EX_TCKT_TenantPlatformTicketContactFilter))
                .catch((err) => reject(err));
        }),

    markTenantPlatformTicketRead: (cmsId, ticketId) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: MarkTenantPlatformTicketReadGql,
                    variables: { cms_id: parseInt(cmsId), ticket_id: parseInt(ticketId) },
                })
                .then(({ data }) => resolve(data.EX_TCKT_MarkTenantPlatformTicketRead))
                .catch((err) => reject(err));
        }),

    attachTenantPlatformTicketFiles: (cmsId, ticketId, files) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: AttachTenantPlatformTicketFilesGql,
                    variables: { cms_id: parseInt(cmsId), ticket_id: parseInt(ticketId), files: fileInputs(files) },
                })
                .then(({ data }) => resolve(data.EX_TCKT_AttachTenantPlatformTicketFiles))
                .catch((err) => reject(err));
        }),
};
